import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ewayApi, type EwayRow, type EwayStateRule } from "../lib/api";
import { useCompany } from "../masters/reference";
import { errText, useLoad } from "../ui/useLoad";
import { fmtAmount } from "../lib/money";
import { ErrorNote, Loading, PageHead } from "../ui/bits";
import { fmtDate } from "../companies/CompanyPicker";
import { toUrl } from "../shell/routes";

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const rupees = (n: number | null | undefined) => (n == null ? "" : `₹${fmtAmount(n).replace(/\.00$/, "")}`);
const SCOPE: Record<EwayStateRule["scope"], string> = { all: "All goods", notified_goods: "Notified goods only", none: "Not needed" };

function state(r: EwayRow): [string, string] {
  if (r.number) return ["Made", "state-filed"];
  if (r.choice === "not_needed") return ["Not needed", "state-upcoming"];
  if (r.status === "check") return ["Check", "state-ready"];
  if (r.choice === "generate") return ["To upload", "state-due"];
  return ["Missing", "state-overdue"];
}

// EW10: the sales invoices that move goods, with their e-way bills
export function EwayRegister() {
  const { company } = useCompany();
  const now = new Date();
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)));
  const [to, setTo] = useState(iso(now));
  const [open, setOpen] = useState(true);
  const { data, error, loading } = useLoad(() => ewayApi.register(company.id, from, to), [company.id, from, to]);
  const rows = useMemo(() => (data ?? []).filter((r) => !open || (!r.number && r.choice !== "not_needed")), [data, open]);
  return (
    <section className="page">
      <PageHead crumb="Compliance" title="E-way bills" code="EW10" />
      <div className="toolbar">
        <label className="inline-label">From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="inline-label">To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <label className="check"><input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} /> Only those still to do</label>
        <Link className="small" to={toUrl("gst/ewaybill-limits")}>Limits by state <span className="code">EW11</span></Link>
      </div>
      <ErrorNote text={error?.message} />
      {loading && !data ? <Loading /> : rows.length === 0 ? <p className="muted">{open ? "Nothing waiting: every invoice that moves goods above the limit has its e-way bill." : "No invoices of goods above the limits in this period."}</p> : (
        <table className="grid-table">
          <thead><tr><th>Date</th><th>Invoice</th><th>Customer</th><th>Movement</th><th className="num">Goods value</th><th className="num">Limit</th><th>E-way bill</th><th></th></tr></thead>
          <tbody>{rows.map((r) => { const [t, c] = state(r); return (
            <tr key={r.id}>
              <td>{fmtDate(r.date)}</td><td><Link to={toUrl(`vouchers/v/${r.id}`)}>{r.number_doc}</Link></td><td>{r.party ?? ""}</td>
              <td className="small">{r.basis === "inter" ? `${r.from_state} → ${r.to_state}` : `Within ${r.from_state}${r.within_city ? ", one city" : ""}`}</td>
              <td className="num">{fmtAmount(r.value ?? 0)}</td><td className="num muted">{rupees(r.limit)}</td>
              <td className="mono small">{r.number ?? (r.choice === "not_needed" ? <span className="muted">{r.choice_reason}</span> : "")}</td>
              <td><span className={`pill ${c}`}>{t}</span></td>
            </tr>); })}</tbody>
        </table>)}
    </section>
  );
}

// EW11: each state's limit for movement within it, and the company's own figure where it knows better
export function EwayLimits() {
  const { company } = useCompany();
  const { data, error, setData } = useLoad(() => ewayApi.limits(company.id), [company.id]);
  const [editing, setEditing] = useState<string | null>(null);
  const [f, setF] = useState<{ scope: string; limit: string; note: string }>({ scope: "all", limit: "", note: "" });
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState("");
  useEffect(() => setErr(null), [editing]);
  if (!data) return <section className="page"><PageHead crumb="Compliance" title="E-way bill limits" code="EW11" />{error ? <ErrorNote text={error.message} /> : <Loading />}</section>;
  const rows = data.states.filter((s) => !q.trim() || s.name.toLowerCase().includes(q.trim().toLowerCase()) || s.code === q.trim());
  const save = (code: string, scope: string | null) => ewayApi.saveLimit(company.id, code, scope, scope && scope !== "none" ? Number(f.limit.replace(/,/g, "")) : null, f.note)
    .then((d) => { setData(d); setEditing(null); }).catch((x) => setErr(errText(x)));
  return (
    <section className="page">
      <PageHead crumb="Compliance" title="E-way bill limits" code="EW11" />
      <p className="muted">Goods moving between states need an e-way bill above <b>{rupees(data.inter_limit)}</b>. Within a state, each state sets its own limit
        (rule 138(14)(d)); Books uses the figures below. When a state notifies a new figure before Books is updated, set your own for that state.</p>
      <div className="toolbar"><input placeholder="Find a state" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a state" /></div>
      <ErrorNote text={err} />
      <table className="grid-table">
        <thead><tr><th>State</th><th>Within the state</th><th className="num">Limit</th><th>Notes</th><th>Your figure</th><th></th></tr></thead>
        <tbody>{rows.map((s) => (
          <tr key={s.code} className={s.mine ? "" : "dim-soft"}>
            <td><span className="mono muted">{s.code}</span> {s.name}{s.mine && <span className="tag">your GSTIN</span>}</td>
            <td>{SCOPE[s.scope]}</td>
            <td className="num">{rupees(s.limit)}{s.city_limit ? <div className="muted small">{rupees(s.city_limit)} within a city</div> : null}</td>
            <td className="muted small">{s.note}</td>
            <td>{editing === s.code ? (
              <div className="fields inline">
                <select value={f.scope} onChange={(e) => setF({ ...f, scope: e.target.value })} aria-label="Which goods">
                  <option value="all">All goods</option><option value="notified_goods">Notified goods only</option><option value="none">Not needed</option></select>
                {f.scope !== "none" && <input className="num short" inputMode="numeric" value={f.limit} placeholder="₹" aria-label="Limit" onChange={(e) => setF({ ...f, limit: e.target.value })} />}
                <input value={f.note} placeholder="Notification / why" aria-label="Note" onChange={(e) => setF({ ...f, note: e.target.value })} />
              </div>) : s.own ? <span>{SCOPE[s.own.scope]}{s.own.limit != null ? ` · ${rupees(s.own.limit)}` : ""}{s.own.note ? <div className="muted small">{s.own.note}</div> : null}</span> : <span className="muted">—</span>}</td>
            <td className="tools">{data.can.edit && (editing === s.code
              ? <><button className="primary small" onClick={() => void save(s.code, f.scope)}>Save</button><button className="link small" onClick={() => setEditing(null)}>Cancel</button></>
              : <><button className="link small" onClick={() => { setEditing(s.code); setF({ scope: s.own?.scope ?? s.scope, limit: String(s.own?.limit ?? s.limit ?? ""), note: s.own?.note ?? "" }); }}>Set your own</button>
                {s.own && <button className="link small danger-link" onClick={() => void save(s.code, null)}>Use the state's</button>}</>)}</td>
          </tr>))}</tbody>
      </table>
      <p className="muted small">Sources: the states' notifications under rule 138(14)(d). Some states exempt particular goods or short distances within a city or district; those details are in the notes.</p>
    </section>
  );
}
